/* eslint-disable react-refresh/only-export-components --
   Widget module: the FormWidget registration and its renderer are one plugin. */
import { FormWidgetService, FormWidgetProps } from "./form-widget-service";

// ── list: rows of one value each, for a field of a checkpoint list type ([Str], [Num], ...) ──
// FEATURE-20261021-check-config-type-and-fileset-form
//
// The value is an array of the rows as typed (strings; the generated form reads each as the list's
// element type and leaves blank rows out). A message about one row ("[1]: ..." from a validate
// message at seeds[1]) shows under that row; any other under the list. An empty list shows one empty
// row, so the first value has somewhere to go.

const ROW_MESSAGE = /^\[(\d+)\]:\s*/;

function rowsOf(value: unknown): string[] {
  if (Array.isArray(value)) return value.map((v) => String(v ?? ""));
  if (typeof value === "string" && value.trim() !== "") return value.split(",").map((s) => s.trim());
  return [];
}

export function ListWidget({ formItem, formData, formErrors, setValue }: FormWidgetProps) {
  const rows = rowsOf(formData[formItem.name]);
  const shown = rows.length === 0 ? [""] : rows;
  const config = (formItem.config ?? {}) as { placeholder?: string; of?: string };
  const disabled = formItem.disabled === true;
  // each message keeps its level, so a note that a server answered is not drawn as an error
  type Msg = { text: string; severity: string };
  const byRow = new Map<number, Msg[]>();
  const rest: Msg[] = [];
  for (const e of formErrors[formItem.name] ?? []) {
    const message = e.message ?? "";
    const severity = e.severity ?? "error";
    const m = ROW_MESSAGE.exec(message);
    if (m) byRow.set(Number(m[1]), [...(byRow.get(Number(m[1])) ?? []), { text: message.replace(ROW_MESSAGE, ""), severity }]);
    else rest.push({ text: message, severity });
  }
  const update = (next: string[]) => setValue(next);
  return (
    <div className="dfr-list" data-testid={`list-${formItem.name}`}>
      {shown.map((row, i) => (
        <div key={i} className="dfr-list-row" style={{ display: "flex", flexDirection: "column", gap: 2, marginBottom: 4 }}>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <input
              type="text"
              value={row}
              placeholder={config.placeholder ?? ""}
              disabled={disabled}
              data-testid={`list-row-${i}`}
              onChange={(e) => {
                const next = [...shown];
                next[i] = e.target.value;
                update(next);
              }}
              onKeyDown={(e) => {
                // Enter on the last row opens the next one
                if (e.key === "Enter" && i === shown.length - 1 && row.trim() !== "") {
                  e.preventDefault();
                  update([...shown, ""]);
                }
              }}
              style={{ flex: 1 }}
            />
            <button
              type="button"
              className="dfr-btn"
              disabled={disabled}
              aria-label={`remove ${row || "row " + (i + 1)}`}
              data-testid={`list-remove-${i}`}
              onClick={() => update(shown.filter((_, j) => j !== i))}
            >
              ×
            </button>
          </div>
          {byRow.get(i)?.map((msg, k) => (
            <div key={k} className={`dfr-field-msg ${msg.severity}`} data-testid={`list-issue-${i}`}>
              {msg.text}
            </div>
          ))}
        </div>
      ))}
      <button type="button" className="dfr-btn" disabled={disabled} data-testid="list-add" onClick={() => update([...shown, ""])}>
        + Add
      </button>
      {rest.map((msg, k) => (
        <div key={k} className={`dfr-field-msg ${msg.severity}`}>
          {msg.text}
        </div>
      ))}
    </div>
  );
}

FormWidgetService.get().register({ name: "list", render: (props) => <ListWidget {...props} /> });
